import { OrganisationEntity } from 'Models/Entities';
import { create } from 'zustand';
import { getAllOrganisations } from 'Services/Api/_HumanWritten/OrganisationsService';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { whiteLabelStore } from '../../Models/WhiteLabelStore';
import { themeStore } from '../../Models/ThemeStore';
import { createJSONStorage, persist } from 'zustand/middleware';
import { IOrganisationEntityAttributes } from 'Models/Entities/OrganisationEntity';
import { store } from '../../Models/Store';

const ORGANISATION_LOCAL_STORE_KEY = 'organisation-store';

interface OrganisationsStore {
	loading: boolean;
	organisations: OrganisationEntity[] | null;
	selectedOrganisation: OrganisationEntity | null;
	setOrganisations: (newOrganisations: OrganisationEntity[] | null) => void;
	setSelectedOrganisation: (newOrganisation: OrganisationEntity | null) => void;
	setLoading: (loading: boolean) => void;
}

export const useOrganisationStore = create(
	persist<OrganisationsStore>(
		(set, get) => ({
			loading: true,
			organisations: [],
			selectedOrganisation: null,
			setOrganisations: (newOrganisations: OrganisationEntity[] | null) =>
				set({ organisations: newOrganisations, loading: false }),
			setSelectedOrganisation: (newOrganisation: OrganisationEntity | null) =>
				set({ selectedOrganisation: newOrganisation }),
			setLoading: (loading: boolean) => set({ loading }),
		}),
		{
			name: ORGANISATION_LOCAL_STORE_KEY,
			storage: createJSONStorage(() => sessionStorage),
		},
	),
);

export const retrieveOrganisationsAsync = async () => {
	const orgStore = useOrganisationStore.getState();

	// Check if organisations already exist in the store
	if (orgStore.organisations && orgStore.organisations.length > 0) {
		const preferredOrganisation = orgStore.organisations.find(org => org.id === orgStore.selectedOrganisation?.id);
		if (preferredOrganisation) {
			switchOrganisationsAsync(preferredOrganisation.id);
			return;
		}
		return;
	}

	// Fetch organisations from the API if not already in the store
	const organisations = await getAllOrganisations();
	orgStore.setOrganisations(organisations);
	orgStore.setSelectedOrganisation(null); // Default to no organisation selected
};

export const getOrganisationStore = () => {
	const selectedOrganisation = sessionStorage.getItem(ORGANISATION_LOCAL_STORE_KEY);
	if (isNotNullOrUndefined(selectedOrganisation)) {
		const parsedData = JSON.parse(selectedOrganisation);

		return parsedData.state;
	}
	return null;
};

export const getOrganisations = (): OrganisationEntity[] => {
	const orgStore = getOrganisationStore();
	if (isNotNullOrUndefined(store)) {
		return (
			orgStore.organisations?.map(
				(x: Partial<IOrganisationEntityAttributes> | undefined) => new OrganisationEntity(x),
			) ?? []
		);
	}
	return [];
};

export const getSelectedOrganisation = () => {
	const orgStore = getOrganisationStore();
	if (isNotNullOrUndefined(store)) {
		return new OrganisationEntity(orgStore.selectedOrganisation);
	}
	return null;
};

export const switchOrganisationsAsync = (id: string) => {
	const organisations = getOrganisations();

	const newOrganisation = organisations?.find(x => x.id === id) ?? organisations[0];

	useOrganisationStore.getState().setSelectedOrganisation(newOrganisation);

	// Set the theme (if any associated with the organisation)
	const configTheme = window.themeData.find(x => x.organisation?.id === newOrganisation.id);
	if (configTheme) {
		themeStore.setTheme(configTheme);
	} else {
		const newConfig = window.themeData.find(x => x.organisation === null);
		themeStore.setTheme(newConfig);
	}

	// Set the whitelabel config (if any associated with the organisation)
	const configGeneral = window.whitelabelData.find(x => x.organisation?.id === newOrganisation.id);
	if (configGeneral) {
		whiteLabelStore.setConfig(configGeneral);
	} else {
		const newConfig = window.whitelabelData.find(x => x.organisation === null);
		whiteLabelStore.setConfig(newConfig);
	}

	// if (location.pathname.includes('/upcoming-events') && !newOrganisation.hasTours) {
	// 	navigate('/');
	// }
	// if (location.pathname.includes('/home-search') && !newOrganisation.hasFerries) {
	// 	navigate('/upcoming-events');
	// }
};
